import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import "./RobotMissionControl.css";
import { io } from "socket.io-client";

const ROBOT_CONFIG = {
    1: {
        id: 1,
        name: "Astrobee",
        shortName: "ASTROBEE",
        logo: "AB",
        missionName: "ASTROBEE SIMULATION",
        robotLabel: "ASTROBEE",
        positionFrame: "body",
        initialState: "STANDBY",
        simulator: "GAZEBO",
        topics: [
            { name: "/tf", type: "tf2_msgs/TFMessage", rate: "30 Hz", status: "ACTIVE" },
            { name: "/odom", type: "nav_msgs/Odometry", rate: "20 Hz", status: "ACTIVE" },
            { name: "/imu/data", type: "sensor_msgs/Imu", rate: "100 Hz", status: "ACTIVE" },
            { name: "/battery_state", type: "sensor_msgs/BatteryState", rate: "10 Hz", status: "ACTIVE" },
            { name: "/cmd_vel", type: "geometry_msgs/Twist", rate: "5 Hz", status: "ACTIVE" },
            { name: "/camera/image_raw", type: "sensor_msgs/Image", rate: "30 Hz", status: "ACTIVE" },
            { name: "/joint_states", type: "sensor_msgs/JointState", rate: "20 Hz", status: "ACTIVE" }
        ]
    },

    3: {
        id: 3,
        name: "Int-Ball2",
        shortName: "INT-BALL2",
        logo: "IB2",
        missionName: "INT-BALL2 SIMULATION",
        robotLabel: "INT-BALL2",
        positionFrame: "body",
        initialState: "STANDBY",
        simulator: "GAZEBO",
        topics: [
            { name: "/clock", type: "rosgraph_msgs/Clock", rate: "30 Hz", status: "ACTIVE" },
            { name: "/nav/true/navigation", type: "ib2_msgs/Navigation", rate: "30 Hz", status: "ACTIVE" },
            { name: "/nav/true/attitude", type: "sim_msgs/Attitude", rate: "30 Hz", status: "ACTIVE" },
            { name: "/tf", type: "tf2_msgs/TFMessage", rate: "30 Hz", status: "ACTIVE" },
            { name: "/tf_static", type: "tf2_msgs/TFMessage", rate: "1 Hz", status: "ACTIVE" }
        ]
    }
};

const HELP_CONFIG = {
    1: {
        title: "ASTROBEE MISSION CONTROL",
        description:
            "Astrobee is a free-flying robotic platform designed for autonomous operations inside the International Space Station.",

        gettingStarted: [
            "Start the simulation using the START button.",
            "Use the VNC view to interact with the Gazebo and RViz simulation.",
            "Monitor the robot position, orientation, power, and velocity.",
            "Select ROS topics to inspect their telemetry.",
            "Use ROS BAG RECORDING to record simulation data.",
            "Use ROS BAG REPLAY to replay a previous simulation.",
            "Use EDIT to stop the simulation and return to the programming environment."
        ],

        controls: {
            start: "Starts or resumes the Astrobee simulation.",
            stop: "Stops the current Astrobee simulation.",
            reset: "Resets the Astrobee simulation and returns the robot to its initial state.",
            edit: "Stops the simulation and returns to the programming environment.",
            openVnc: "Opens the simulator desktop in a separate browser window."
        },

        telemetry: {
            position: "Displays Astrobee position along the X, Y, and Z axes in meters.",
            orientation: "Displays Astrobee roll, pitch, and yaw orientation in degrees.",
            quaternion: "Displays Astrobee orientation using quaternion X, Y, Z, and W components.",
            power: "Displays battery percentage, voltage, and current.",
            velocity: "Displays Astrobee linear and angular velocity."
        },

        ros:
            "ROS topics provide telemetry, sensor data, and commands used by the Astrobee simulation."
    },

    3: {
        title: "INT-BALL2 MISSION CONTROL",
        description:
            "Int-Ball2 is a free-flying robotic camera system developed for operations inside the International Space Station.",

        gettingStarted: [
            "Start the simulation using the START button.",
            "Use the VNC view to interact with the Gazebo and RViz simulation.",
            "Monitor the robot position, orientation, power, and velocity.",
            "Select ROS topics to inspect their telemetry.",
            "Use ROS BAG RECORDING to record simulation data.",
            "Use ROS BAG REPLAY to replay a previous simulation.",
            "Use EDIT to stop the simulation and return to the programming environment."
        ],

        controls: {
            start: "Starts or resumes the Int-Ball2 simulation.",
            stop: "Stops the current Int-Ball2 simulation.",
            reset: "Resets the Int-Ball2 simulation and returns the robot to its initial state.",
            edit: "Stops the simulation and returns to the programming environment.",
            openVnc: "Opens the simulator desktop in a separate browser window."
        },

        telemetry: {
            position: "Displays Int-Ball2 position along the X, Y, and Z axes in meters.",
            orientation: "Displays Int-Ball2 roll, pitch, and yaw orientation in degrees.",
            quaternion: "Displays Int-Ball2 orientation using quaternion X, Y, Z, and W components.",
            power: "Displays battery percentage, voltage, and current.",
            velocity: "Displays Int-Ball2 linear and angular velocity."
        },

        ros:
            "ROS topics provide telemetry, sensor data, and commands used by the Int-Ball2 simulation."
    }
};

const DEFAULT_ROBOT_STATE = {
    timestamp: 0,
    robot_id: "astrobee",
    mission_id: "",
    connection: "CONNECTED",
    simulation: "RUNNING",
    state: "STANDBY",

    position: {
        x: 0,
        y: 0,
        z: 0
    },

    orientation: {
        x: 0,
        y: 0,
        z: 0,
        w: 1
    },

    rpy: {
        roll: 0,
        pitch: 0,
        yaw: 0
    },

    velocity: {
        x: 0,
        y: 0,
        z: 0,
        linear: 0,
        angular: 0
    },

    speed: 0,

    battery: {
        percentage: 0,
        voltage: 0,
        current: 0
    },

    system: {
        cpu_percent: 0,
        memory_percent: 0,
        memory_used_bytes: 0,
        memory_available_bytes: 0,
        memory_total_bytes: 0
    },

    ros: {
        node: "...",
        topics: []
    }
};


const RobotMissionControl = ({ vncUrl }) => {

    const { lab_id } = useParams();
    const {projectId} = useParams();

    const robotId = Number(lab_id);

    const robot = useMemo(
        () => ROBOT_CONFIG[robotId] || ROBOT_CONFIG[3],
        [robotId]
    );

    const helpConfig =
        HELP_CONFIG[robotId] || HELP_CONFIG[3];

    const [activeCenterTab, setActiveCenterTab] =
        useState("vnc");

    const [selectedTopic, setSelectedTopic] =
        useState(null);

    const [showHelp, setShowHelp] =
        useState(false);

    const [robotState, setRobotState] =
        useState(DEFAULT_ROBOT_STATE);

    /*
     * ROS BAG STATE
     */

    const [rosbags, setRosbags] =
        useState([]);

    const [selectedBag, setSelectedBag] =
        useState("");

    const [replayRate, setReplayRate] =
        useState(1.0);

    const [replayStatus, setReplayStatus] =
        useState("STOPPED");

    const [recordingStatus, setRecordingStatus] =
        useState("STOPPED");

    const [recordingBag, setRecordingBag] =
        useState(null);

    const [recordingBusy, setRecordingBusy] =
        useState(false);

    const [replayBusy, setReplayBusy] =
        useState(false);


    /*
     * RESET ROBOT STATE WHEN ROBOT CHANGES
     */

    useEffect(() => {

        setRobotState({
            ...DEFAULT_ROBOT_STATE,

            robot_id:
                robotId === 3
                    ? "intball2"
                    : "astrobee",

            ros: {
                node: "...",
                topics: robot.topics
            }
        });

        setSelectedTopic(null);

        setRosbags([]);

        setSelectedBag("");

        setReplayStatus("STOPPED");

        setRecordingStatus("STOPPED");

        setRecordingBag(null);

    }, [robot, robotId]);


    /*
     * ESCAPE CLOSES HELP
     */

    useEffect(() => {

        const handleKeyDown = event => {

            if (event.key === "Escape") {
                setShowHelp(false);
            }

        };

        window.addEventListener(
            "keydown",
            handleKeyDown
        );

        return () => {

            window.removeEventListener(
                "keydown",
                handleKeyDown
            );

        };

    }, []);


    /*
     * SOCKET.IO TELEMETRY
     */

    useEffect(() => {

        const socket = io(
            window.location.origin,
            {
                transports: ["polling"]
            }
        );

        socket.on("connect", () => {

            console.log(
                "Connected to STEMX365 Socket.IO:",
                socket.id
            );

        });


        socket.on(
            "robot.telemetry",
            telemetry => {

                // console.log(
                //     "TELEMETRY:::",
                //     telemetry
                // );

                setRobotState(previous => ({
                    ...previous,
                    ...telemetry
                }));

            }
        );


        socket.on("disconnect", () => {

            console.log(
                "Disconnected from STEMX365 telemetry"
            );

        });


        socket.on(
            "connect_error",
            error => {

                console.error(
                    "Socket.IO connection error:",
                    error
                );

            }
        );


        return () => {

            socket.off(
                "robot.telemetry"
            );

            socket.disconnect();

        };

    }, []);


    /*
     * LOAD ROS BAGS
     */

    useEffect(() => {

        const loadBags = async () => {

            try {

                const response =
                    await fetch(
                        "/api/simulate/recording",
                        {
                            method: "POST",

                            headers: {
                                "Content-Type":
                                    "application/json"
                            },

                            body: JSON.stringify({
                                lab_id,
                                robot_id: robotId,
                                action: "list"
                            })
                        }
                    );

                const result =
                    await response.json();

                if (!response.ok) {

                    throw new Error(
                        result.message ||
                        `HTTP ${response.status}`
                    );

                }

                const bags =
                    result.bags || [];

                setRosbags(bags);

                if (
                    bags.length > 0 &&
                    !selectedBag
                ) {

                    setSelectedBag(
                        bags[0].bag_name
                    );

                }

            } catch (error) {

                console.error(
                    "Unable to load ROS bags:",
                    error
                );

            }

        };

        if (lab_id && robotId) {
            loadBags();
        }

    }, [lab_id, robotId]);


    /*
     * VNC URL
     */

    const embeddedVncUrl =
        vncUrl
            ? `${vncUrl}${vncUrl.includes("?") ? "&" : "?"}embedded=true`
            : "";


    /*
     * OPEN VNC
     */

    const openVNCWindow = () => {

        window.open(
            vncUrl,
            `${robot.shortName}VNC`,
            "width=1600,height=1000,resizable=yes,scrollbars=no"
        );

    };


    /*
     * SIMULATION CONTROL
     */

    const sendCommand = async command => {

        try {
console.log("EDIT:::  ", command);
            const response =
                await fetch(
                    "/api/simulate/control",
                    {
                        method: "POST",

                        headers: {
                            "Content-Type":
                                "application/json"
                        },

                        body: JSON.stringify({
                            lab_id,
                            action: command
                        })
                    }
                );

            if (!response.ok) {

                throw new Error(
                    `HTTP ${response.status}: ${response.statusText}`
                );

            }

            const result =
                await response.json();

            console.log(
                `[${robot.name}] ${command} response:`,
                result
            );

            if (
                command === "abort" &&
                result.status === "accepted"
            ) {

                window.location.href =
                    `/rect/ide/${lab_id}/${projectId}`;

            }

            return result;

        } catch (error) {

            console.error(
                `[${robot.name}] Command error:`,
                error
            );

            throw error;

        }

    };


    /*
     * ROS BAG API
     */

    const recordingControl =
        async (action, extra = {}) => {
            console.log("ACTION:    ", action);

            const response =
                await fetch(
                    "/api/simulate/recording",
                    {
                        method: "POST",

                        headers: {
                            "Content-Type":
                                "application/json"
                        },

                        body: JSON.stringify({
                            lab_id,
                            robot_id: robotId,
                            action,
                            ...extra
                        })
                    }
                );

            const result =
                await response.json();

            if (!response.ok) {

                throw new Error(
                    result.message ||
                    `HTTP ${response.status}`
                );

            }
            console.log("RESULT:::::", result);
            return result;
        };


    /*
     * REFRESH BAGS
     */

    const loadRosbags = async () => {

        try {

            const result =
                await recordingControl(
                    "list"
                );

            const bags =
                result.bags || [];

            setRosbags(bags);

            if (
                bags.length > 0 &&
                !selectedBag
            ) {

                setSelectedBag(
                    bags[0].bag_name
                );

            }

        } catch (error) {

            console.error(
                "Unable to load ROS bags:",
                error
            );

        }

    };


    /*
     * START RECORDING
     */

    const startRecording = async () => {

        if (recordingBusy) {
            return;
        }

        try {

            setRecordingBusy(true);

            const result =
                await recordingControl(
                    "start"
                );

            setRecordingStatus(
                "RECORDING"
            );

            setRecordingBag(
                result.bag_name
            );

            console.log(
                "ROS bag recording started:",
                result
            );

        } catch (error) {

            console.error(
                "Start recording error:",
                error
            );

            alert(
                `Unable to start recording:\n${error.message}`
            );

        } finally {

            setRecordingBusy(false);

        }

    };


    /*
     * STOP RECORDING
     */

    const stopRecording = async () => {

        if (recordingBusy) {
            return;
        }

        try {

            setRecordingBusy(true);

            const result =
                await recordingControl(
                    "stop"
                );

            setRecordingStatus(
                "STOPPED"
            );

            setRecordingBag(null);

            console.log(
                "ROS bag saved:",
                result
            );

            await loadRosbags();

        } catch (error) {

            console.error(
                "Stop recording error:",
                error
            );

            alert(
                `Unable to stop recording:\n${error.message}`
            );

        } finally {

            setRecordingBusy(false);

        }

    };


    /*
     * START REPLAY
     */

    const startReplay = async () => {

        if (replayBusy) {
            return;
        }

        if (!selectedBag) {

            alert(
                "Please select a ROS bag first."
            );

            return;
        }

        try {

            setReplayBusy(true);

            const result =
                await recordingControl(
                    "replay_start",
                    {
                        bag_name:
                            selectedBag,

                        rate:
                            replayRate
                    }
                );

            setReplayStatus(
                "REPLAYING"
            );

            console.log(
                "ROS bag replay started:",
                result
            );

        } catch (error) {

            console.error(
                "Start replay error:",
                error
            );

            alert(
                `Unable to start replay:\n${error.message}`
            );

        } finally {

            setReplayBusy(false);

        }

    };


    /*
     * STOP REPLAY
     */

    const stopReplay = async () => {

        if (replayBusy) {
            return;
        }

        try {

            setReplayBusy(true);

            const result =
                await recordingControl(
                    "replay_stop"
                );

            setReplayStatus(
                "STOPPED"
            );

            console.log(
                "ROS bag replay stopped:",
                result
            );

        } catch (error) {

            console.error(
                "Stop replay error:",
                error
            );

            alert(
                `Unable to stop replay:\n${error.message}`
            );

        } finally {

            setReplayBusy(false);

        }

    };


    return (

        <div className="intball-mission-control">


            {/* ============================================================
                HELP
            ============================================================ */}

            {showHelp && (

                <div
                    className="help-overlay"
                    onClick={() =>
                        setShowHelp(false)
                    }
                >

                    <div
                        className="help-modal"
                        onClick={event =>
                            event.stopPropagation()
                        }
                    >

                        <div className="help-header">

                            <div>

                                <div className="help-title">
                                    {helpConfig.title}
                                </div>

                                <div className="help-subtitle">
                                    {robot.name} Simulation
                                </div>

                            </div>

                            <button
                                className="help-close"
                                onClick={() =>
                                    setShowHelp(false)
                                }
                                aria-label="Close help"
                            >
                                ×
                            </button>

                        </div>


                        <div className="help-content">


                            <section className="help-section">

                                <h3>
                                    ABOUT {robot.shortName}
                                </h3>

                                <p className="help-description">
                                    {helpConfig.description}
                                </p>

                            </section>


                            <section className="help-section">

                                <h3>
                                    GETTING STARTED
                                </h3>

                                <div className="help-steps">

                                    {helpConfig.gettingStarted.map(
                                        (step, index) => (

                                            <div
                                                className="help-step"
                                                key={index}
                                            >

                                                <div className="help-step-number">
                                                    {index + 1}
                                                </div>

                                                <div className="help-step-text">
                                                    {step}
                                                </div>

                                            </div>

                                        )
                                    )}

                                </div>

                            </section>


                            <section className="help-section">

                                <h3>
                                    ROBOT CONTROL
                                </h3>

                                <div className="help-item">
                                    <strong>START</strong>
                                    <span>
                                        {helpConfig.controls.start}
                                    </span>
                                </div>

                                <div className="help-item">
                                    <strong>STOP</strong>
                                    <span>
                                        {helpConfig.controls.stop}
                                    </span>
                                </div>

                                <div className="help-item">
                                    <strong>RESET</strong>
                                    <span>
                                        {helpConfig.controls.reset}
                                    </span>
                                </div>

                                <div className="help-item">
                                    <strong>EDIT</strong>
                                    <span>
                                        {helpConfig.controls.edit}
                                    </span>
                                </div>

                                <div className="help-item">
                                    <strong>OPEN VNC</strong>
                                    <span>
                                        {helpConfig.controls.openVnc}
                                    </span>
                                </div>

                            </section>


                            <section className="help-section">

                                <h3>
                                    ROS BAG
                                </h3>

                                <div className="help-item">

                                    <strong>
                                        RECORDING
                                    </strong>

                                    <span>
                                        Records selected ROS
                                        telemetry topics into
                                        a ROS bag.
                                    </span>

                                </div>

                                <div className="help-item">

                                    <strong>
                                        REPLAY
                                    </strong>

                                    <span>
                                        Replays a previously
                                        recorded ROS bag using
                                        simulated time.
                                    </span>

                                </div>

                                <div className="help-item">

                                    <strong>
                                        PLAYBACK RATE
                                    </strong>

                                    <span>
                                        Controls the ROS bag
                                        replay speed.
                                    </span>

                                </div>

                            </section>


                            <section className="help-section">

                                <h3>
                                    SIMULATION VIEW
                                </h3>

                                <div className="help-item">

                                    <strong>
                                        VNC
                                    </strong>

                                    <span>
                                        Displays the simulator
                                        desktop containing
                                        Gazebo and RViz.
                                    </span>

                                </div>

                                <div className="help-item">

                                    <strong>
                                        NON-VNC
                                    </strong>

                                    <span>
                                        Displays robot telemetry
                                        without the simulator
                                        desktop.
                                    </span>

                                </div>

                                <div className="help-item">

                                    <strong>
                                        OPEN VNC
                                    </strong>

                                    <span>
                                        Opens the simulator desktop
                                        in a separate browser window.
                                    </span>

                                </div>

                            </section>


                            <section className="help-section">

                                <h3>
                                    ROBOT TELEMETRY
                                </h3>

                                <div className="help-item">
                                    <strong>POSITION</strong>
                                    <span>
                                        {helpConfig.telemetry.position}
                                    </span>
                                </div>

                                <div className="help-item">
                                    <strong>ORIENTATION</strong>
                                    <span>
                                        {helpConfig.telemetry.orientation}
                                    </span>
                                </div>

                                <div className="help-item">
                                    <strong>QUATERNION</strong>
                                    <span>
                                        {helpConfig.telemetry.quaternion}
                                    </span>
                                </div>

                                <div className="help-item">
                                    <strong>POWER SYSTEM</strong>
                                    <span>
                                        {helpConfig.telemetry.power}
                                    </span>
                                </div>

                                <div className="help-item">
                                    <strong>VELOCITY</strong>
                                    <span>
                                        {helpConfig.telemetry.velocity}
                                    </span>
                                </div>

                            </section>


                            <section className="help-section">

                                <h3>
                                    ROS TOPICS
                                </h3>

                                <div className="help-item">

                                    <strong>
                                        TOPICS
                                    </strong>

                                    <span>
                                        ROS telemetry and
                                        simulation topics.
                                    </span>

                                </div>

                                <div className="help-item">

                                    <strong>
                                        SELECT TOPIC
                                    </strong>

                                    <span>
                                        Select a topic to view
                                        its message type,
                                        publishing rate, status,
                                        and latest message.
                                    </span>

                                </div>

                            </section>


                            <section className="help-section">

                                <h3>
                                    SYSTEM STATUS
                                </h3>

                                <div className="help-item">

                                    <strong>
                                        CONNECTED
                                    </strong>

                                    <span>
                                        Mission Control is
                                        connected to the ROS
                                        environment.
                                    </span>

                                </div>

                                <div className="help-item">

                                    <strong>
                                        RUNNING
                                    </strong>

                                    <span>
                                        The simulator is
                                        currently running.
                                    </span>

                                </div>

                                <div className="help-item">

                                    <strong>
                                        ROS NOETIC
                                    </strong>

                                    <span>
                                        The simulation is running
                                        using ROS Noetic.
                                    </span>

                                </div>

                            </section>

                        </div>


                        <div className="help-footer">

                            <span>
                                Mission Control
                            </span>

                            <button
                                className="help-ok-button"
                                onClick={() =>
                                    setShowHelp(false)
                                }
                            >
                                CLOSE
                            </button>

                        </div>

                    </div>

                </div>

            )}


            {/* ============================================================
                NAVIGATION
            ============================================================ */}

            <header className="mission-nav">

                <div className="mission-nav-left">

                    <div className="mission-logo">

                        <div className="mission-logo-circle">
                            {robot.logo}
                        </div>

                        <div>

                            <div className="mission-title">
                                {robot.name}
                            </div>

                            <div className="mission-subtitle">
                                MISSION CONTROL
                            </div>

                        </div>

                    </div>

                    <div className="mission-status">

                        <span className="status-dot status-green"></span>

                        SYSTEM ONLINE

                    </div>

                </div>


                <div className="mission-nav-center">

                    <div className="mission-name">
                        {robot.missionName}
                    </div>

                    <div className="mission-clock">
                        SIMULATION TIME&nbsp;&nbsp;
                        {robotState.timestamp}
                    </div>

                </div>


                <div className="mission-nav-right">

                    <button
                        className="nav-button vnc-button"
                        onClick={openVNCWindow}
                    >
                        <span className="button-icon">
                            ↗
                        </span>
                        OPEN VNC
                    </button>

                    <button
                        className="nav-button help-button"
                        onClick={() =>
                            setShowHelp(true)
                        }
                    >
                        ?
                        <span>
                            HELP
                        </span>
                    </button>

                    <button
                        className="nav-button"
                        onClick={() =>
                            sendCommand("abort")
                        }
                    >
                        EDIT
                    </button>

                </div>

            </header>


            {/* ============================================================
                MAIN
            ============================================================ */}

            <main className="mission-main">


                {/* ========================================================
                    LEFT PANEL
                ======================================================== */}

                <aside className="left-panel">


                    <section className="panel-section">

                        <div className="mission-section-header">

                            <span>
                                ROBOT STATUS
                            </span>

                            <span className="online-badge">
                                ONLINE
                            </span>

                        </div>

                        <div className="status-card">

                            <div className="status-row">
                                <span>
                                    Connection
                                </span>

                                <strong className="value-green">
                                    {robotState.connection}
                                </strong>
                            </div>

                            <div className="status-row">
                                <span>
                                    Simulation
                                </span>

                                <strong className="value-green">
                                    {robotState.simulation}
                                </strong>
                            </div>

                            <div className="status-row">
                                <span>
                                    Robot State
                                </span>

                                <strong>
                                    {robotState.state}
                                </strong>
                            </div>

                        </div>

                    </section>


                    <section className="panel-section">

                        <div className="mission-section-header">
                            POSITION
                        </div>

                        <div className="telemetry-grid">

                            <TelemetryValue
                                label="X"
                                value={robotState.position?.x}
                                unit="m"
                            />

                            <TelemetryValue
                                label="Y"
                                value={robotState.position?.y}
                                unit="m"
                            />

                            <TelemetryValue
                                label="Z"
                                value={robotState.position?.z}
                                unit="m"
                            />

                        </div>

                    </section>


                    <section className="panel-section">

                        <div className="mission-section-header">
                            ORIENTATION
                        </div>

                        <div className="telemetry-grid">

                            <TelemetryValue
                                label="ROLL"
                                value={robotState.rpy?.roll}
                                unit="°"
                            />

                            <TelemetryValue
                                label="PITCH"
                                value={robotState.rpy?.pitch}
                                unit="°"
                            />

                            <TelemetryValue
                                label="YAW"
                                value={robotState.rpy?.yaw}
                                unit="°"
                            />

                        </div>

                    </section>


                    <section className="panel-section">

                        <div className="mission-section-header">
                            QUATERNION
                        </div>

                        <div className="quaternion-grid">

                            <TelemetryValue
                                label="X"
                                value={robotState.orientation?.x}
                            />

                            <TelemetryValue
                                label="Y"
                                value={robotState.orientation?.y}
                            />

                            <TelemetryValue
                                label="Z"
                                value={robotState.orientation?.z}
                            />

                            <TelemetryValue
                                label="W"
                                value={robotState.orientation?.w}
                            />

                        </div>

                    </section>


                    <section className="panel-section">

                        <div className="mission-section-header">
                            POWER SYSTEM
                        </div>

                        <div className="battery-container">

                            <div className="battery-top">

                                <span>
                                    BATTERY
                                </span>

                                <strong>
                                    {typeof robotState.battery === "object"
                                        ? `${robotState.battery?.percentage ?? 0}%`
                                        : `${robotState.battery ?? 0}%`
                                    }
                                </strong>

                            </div>

                            <div className="battery-bar">

                                <div
                                    className="battery-level"
                                    style={{
                                        width: `${
                                            typeof robotState.battery === "object"
                                                ? robotState.battery?.percentage ?? 0
                                                : robotState.battery ?? 0
                                        }%`
                                    }}
                                />

                            </div>

                        </div>


                        <div className="power-grid">

                            <TelemetryValue
                                label="VOLTAGE"
                                value={
                                    typeof robotState.battery === "object"
                                        ? robotState.battery?.voltage
                                        : robotState.voltage
                                }
                                unit="V"
                            />

                            <TelemetryValue
                                label="CURRENT"
                                value={
                                    typeof robotState.battery === "object"
                                        ? robotState.battery?.current
                                        : robotState.current
                                }
                                unit="A"
                            />

                        </div>

                    </section>


                    <section className="panel-section">

                        <div className="mission-section-header">
                            VELOCITY
                        </div>

                        <div className="telemetry-grid">

                            <TelemetryValue
                                label="LINEAR"
                                value={
                                    robotState.velocity?.linear ??
                                    robotState.speed
                                }
                                unit="m/s"
                            />

                            <TelemetryValue
                                label="ANGULAR"
                                value={
                                    robotState.velocity?.angular
                                }
                                unit="rad/s"
                            />

                        </div>

                    </section>

                </aside>


                {/* ========================================================
                    CENTER PANEL
                ======================================================== */}

                <section className="center-panel">

                    <div className="center-header">

                        <div className="center-title">
                            ROBOT CONTROL
                        </div>

                        <div className="center-actions">

                            <button
                                className="robot-control-button start"
                                onClick={() =>
                                    sendCommand("start")
                                }
                            >
                                START
                            </button>

                            <button
                                className="robot-control-button stop"
                                onClick={() =>
                                    sendCommand("stop")
                                }
                            >
                                STOP
                            </button>

                            <button
                                className="robot-control-button reset"
                                onClick={() =>
                                    sendCommand("reset")
                                }
                            >
                                RESET
                            </button>
                             <button
                                className="robot-control-button reset"
                                onClick={() =>
                                    sendCommand("restart_ros_gazebo")
                                }
                            >
                                RESTART
                            </button>

                        </div>

                    </div>


                    <div className="simulation-tabs">

                        <button
                            className={
                                activeCenterTab === "vnc"
                                    ? "simulation-tab active"
                                    : "simulation-tab"
                            }
                            onClick={() =>
                                setActiveCenterTab("vnc")
                            }
                        >
                            VNC
                        </button>

                        <button
                            className={
                                activeCenterTab === "non-vnc"
                                    ? "simulation-tab active"
                                    : "simulation-tab"
                            }
                            onClick={() =>
                                setActiveCenterTab("non-vnc")
                            }
                        >
                            NON-VNC
                        </button>

                    </div>


                    <div className="simulation-container">

                        {activeCenterTab === "vnc" && (

                            <iframe
                                title={`${robot.name} VNC`}
                                className="vnc-frame"
                                src={embeddedVncUrl}
                                allow="clipboard-read; clipboard-write"
                            />

                        )}


                        {activeCenterTab === "non-vnc" && (

                            <div className="non-vnc-view">

                                <div className="non-vnc-header">
                                    {robot.name.toUpperCase()}
                                    {" "}
                                    TELEMETRY VIEW
                                </div>

                                <div className="non-vnc-content">

                                    <div className="robot-placeholder">

                                        <div className="robot-circle">
                                            {robot.logo}
                                        </div>

                                        <div className="robot-label">
                                            {robot.robotLabel}
                                        </div>

                                        <div className="robot-position">

                                            X:
                                            {" "}
                                            {robotState.position?.x?.toFixed(3)}
                                            {" "}m

                                            <br />

                                            Y:
                                            {" "}
                                            {robotState.position?.y?.toFixed(3)}
                                            {" "}m

                                            <br />

                                            Z:
                                            {" "}
                                            {robotState.position?.z?.toFixed(3)}
                                            {" "}m

                                        </div>

                                    </div>

                                </div>

                            </div>

                        )}

                    </div>


                    <div className="center-footer">

                        <div>
                            ROS MASTER
                            {" "}
                            <span className="footer-value">
                                CONNECTED
                            </span>
                        </div>

                        <div>
                            {robot.simulator}
                            {" "}
                            <span className="footer-value">
                                RUNNING
                            </span>
                        </div>

                        <div>
                            ROS
                            {" "}
                            <span className="footer-value">
                                NOETIC
                            </span>
                        </div>

                        <div>
                            FPS
                            {" "}
                            <span className="footer-value">
                                30
                            </span>
                        </div>

                    </div>

                </section>


                {/* ========================================================
                    RIGHT PANEL
                ======================================================== */}

                <aside className="right-panel">

                        {/* ====================================================
                        ROS BAG RECORDING
                    ==================================================== */}

                    <section className="panel-section rosbag-panel">

                        <div className="mission-section-header">

                            <span>
                                ROS BAG RECORDING
                            </span>

                            <span
                                className={
                                    recordingStatus === "RECORDING"
                                        ? "online-badge recording-active"
                                        : "online-badge"
                                }
                            >
                                {recordingStatus}
                            </span>

                        </div>


                        {recordingBag && (

                            <div className="recording-file">

                                <span>
                                    RECORDING
                                </span>

                                <strong>
                                    {recordingBag}
                                </strong>

                            </div>

                        )}


                        <div className="rosbag-actions">

                            {recordingStatus !== "RECORDING" ? (

                                <button
                                    className="rosbag-button record-start"
                                    onClick={startRecording}
                                    disabled={recordingBusy}
                                >
                                    ● START RECORDING
                                </button>

                            ) : (

                                <button
                                    className="rosbag-button record-stop"
                                    onClick={stopRecording}
                                    disabled={recordingBusy}
                                >
                                    ■ STOP RECORDING
                                </button>

                            )}

                        </div>

                    </section>


                    {/* ====================================================
                        ROS BAG REPLAY
                    ==================================================== */}

                    <section className="panel-section rosbag-panel">

                        <div className="mission-section-header">

                            <span>
                                ROS BAG REPLAY
                            </span>

                            <span
                                className={
                                    replayStatus === "REPLAYING"
                                        ? "online-badge replay-active"
                                        : "online-badge"
                                }
                            >
                                {replayStatus}
                            </span>

                        </div>


                        <div className="rosbag-field">

                            <label>
                                MISSION RECORDING
                            </label>

                            <select
                                className="rosbag-select"
                                value={selectedBag}
                                onChange={event =>
                                    setSelectedBag(
                                        event.target.value
                                    )
                                }
                                disabled={
                                    replayStatus === "REPLAYING"
                                }
                            >

                                <option value="">
                                    SELECT ROS BAG
                                </option>

                                {rosbags.map(
                                    bag => (

                                        <option
                                            key={bag.bag_name}
                                            value={bag.bag_name}
                                        >
                                            {bag.bag_name}
                                        </option>

                                    )
                                )}

                            </select>

                        </div>


                        {selectedBag && (

                            <div className="rosbag-info">

                                <span>
                                    SIZE
                                </span>

                                <strong>

                                    {rosbags.find(
                                        bag =>
                                            bag.bag_name ===
                                            selectedBag
                                    )?.size || "--"}

                                </strong>

                            </div>

                        )}


                        <div className="rosbag-field">

                            <label>
                                PLAYBACK RATE
                            </label>

                            <div className="replay-rate-buttons">

                                {[0.5, 1.0, 2.0].map(
                                    rate => (

                                        <button
                                            key={rate}
                                            className={
                                                replayRate === rate
                                                    ? "replay-rate active"
                                                    : "replay-rate"
                                            }
                                            onClick={() =>
                                                setReplayRate(
                                                    rate
                                                )
                                            }
                                            disabled={
                                                replayStatus ===
                                                "REPLAYING"
                                            }
                                        >
                                            {rate}x
                                        </button>

                                    )
                                )}

                            </div>

                        </div>


                        <div className="rosbag-actions">

                            {replayStatus !== "REPLAYING" ? (

                                <button
                                    className="rosbag-button replay-start"
                                    onClick={startReplay}
                                    disabled={
                                        !selectedBag ||
                                        replayBusy
                                    }
                                >
                                    ▶ REPLAY
                                </button>

                            ) : (

                                <button
                                    className="rosbag-button replay-stop"
                                    onClick={stopReplay}
                                    disabled={replayBusy}
                                >
                                    ■ STOP REPLAY
                                </button>

                            )}

                        </div>

                    </section>



                    {/* ====================================================
                        ROS TOPICS
                    ==================================================== */}

                    <section className="panel-section topic-section">

                        <div className="mission-section-header">

                            <span>
                                ROS TOPICS
                            </span>

                            <span className="topic-count">
                                {robotState.ros?.topics?.length || 0}
                            </span>

                        </div>

                        <div className="topic-list">

                            {(robotState.ros?.topics || []).map(
                                topic => (

                                    <button
                                        key={topic.name}
                                        className={
                                            selectedTopic?.name === topic.name
                                                ? "topic-item selected"
                                                : "topic-item"
                                        }
                                        onClick={() =>
                                            setSelectedTopic(topic)
                                        }
                                    >

                                        <div className="topic-name">

                                            <span className="status-dot status-green"></span>

                                            {topic.name}

                                        </div>

                                        <div className="topic-info">

                                            <span>
                                                {topic.type}
                                            </span>

                                            <span>
                                                {topic.rate}
                                            </span>

                                        </div>

                                    </button>

                                )
                            )}

                        </div>

                    </section>


                    {/* ====================================================
                        TOPIC DETAILS
                    ==================================================== */}

                    <section className="panel-section topic-details">

                        <div className="mission-section-header">
                            TOPIC TELEMETRY
                        </div>

                        {selectedTopic ? (

                            <div className="selected-topic">

                                <div className="selected-topic-name">
                                    {selectedTopic.name}
                                </div>

                                <div className="detail-row">
                                    <span>Type</span>
                                    <strong>
                                        {selectedTopic.type}
                                    </strong>
                                </div>

                                <div className="detail-row">
                                    <span>Rate</span>
                                    <strong>
                                        {selectedTopic.rate}
                                    </strong>
                                </div>

                                <div className="detail-row">
                                    <span>Status</span>
                                    <strong className="value-green">
                                        {selectedTopic.status}
                                    </strong>
                                </div>

                                <div className="message-box">

                                    <div className="message-header">
                                        LATEST MESSAGE
                                    </div>

                                    <pre>
{`{
  "header": {
    "stamp": "${robotState.timestamp}"
  },
  "frame_id": "${robot.positionFrame}",
  "data": {
    "x": ${robotState.position?.x?.toFixed(4)},
    "y": ${robotState.position?.y?.toFixed(4)},
    "z": ${robotState.position?.z?.toFixed(4)}
  }
}`}
                                    </pre>

                                </div>

                            </div>

                        ) : (

                            <div className="no-topic">

                                Select a ROS topic
                                <br />
                                to view telemetry.

                            </div>

                        )}

                    </section>


                    {/* ====================================================
                        SYSTEM MESSAGES
                    ==================================================== */}

                    <section className="panel-section">

                        <div className="mission-section-header">
                            SYSTEM MESSAGES
                        </div>

                        <div className="system-messages">

                            <div className="system-message">

                                <span className="message-time">
                                    00:14:31
                                </span>

                                <span>
                                    Simulation running
                                </span>

                            </div>

                            <div className="system-message">

                                <span className="message-time">
                                    00:14:29
                                </span>

                                <span>
                                    ROS master connected
                                </span>

                            </div>

                            <div className="system-message">

                                <span className="message-time">
                                    00:14:25
                                </span>

                                <span>
                                    {robot.name} initialized
                                </span>

                            </div>

                        </div>

                    </section>

                </aside>

            </main>

        </div>
    );
};


/*
 * TELEMETRY VALUE
 */

const TelemetryValue = ({
    label,
    value,
    unit = ""
}) => {

    const numericValue =
        typeof value === "number"
            ? value.toFixed(3)
            : value ?? "--";

    return (

        <div className="telemetry-value">

            <div className="telemetry-label">
                {label}
            </div>

            <div className="telemetry-number">

                {numericValue}

                {unit && (
                    <span className="telemetry-unit">
                        {unit}
                    </span>
                )}

            </div>

        </div>

    );
};


export default RobotMissionControl;